<template>
  <div class="side-navigation">
    <a-menu
      v-model:selectedKeys="selectedKeys"
      v-model:openKeys="openKeys"
      mode="inline"
      theme="light"
      class="navigation-menu"
      @click="handleMenuClick"
    >
      <template v-for="menu in filteredMenus" :key="menu.key">
        <!-- 有子菜单的项目 -->
        <a-sub-menu v-if="menu.children && menu.children.length > 0" :key="menu.key">
          <template #icon>
            <component :is="getIconComponent(menu.icon)" v-if="menu.icon" />
          </template>
          <template #title>{{ resolveMenuTitle(menu) }}</template>
          
          <a-menu-item 
            v-for="child in menu.children" 
            :key="child.path"
            @click="() => handleMenuClick({ key: child.path })"
          >
            <template #icon>
              <component :is="getIconComponent(child.icon)" v-if="child.icon" />
            </template>
            {{ resolveMenuTitle(child) }}
          </a-menu-item>
        </a-sub-menu>
        
        <!-- 没有子菜单的项目 -->
        <a-menu-item v-else :key="menu.path">
          <template #icon>
            <component :is="getIconComponent(menu.icon)" v-if="menu.icon" />
          </template>
          {{ resolveMenuTitle(menu) }}
        </a-menu-item>
      </template>
    </a-menu>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import type { RouteRecordRaw } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore, useAppStore } from '@/stores'
import appRouter from '@/router'
import { permissionManager } from '@/utils/permission'
import {
  DashboardOutlined,
  ProjectOutlined,
  VideoCameraOutlined,
  FileTextOutlined,
  CheckSquareOutlined,
  SettingOutlined,
  UserOutlined,
  FolderOutlined,
  FileAddOutlined,
  UnorderedListOutlined,
  SendOutlined,
  InboxOutlined,
  TeamOutlined,
  ApartmentOutlined,
  SafetyOutlined,
  DatabaseOutlined,
  ApiOutlined,
  BookOutlined,
  ControlOutlined,
  IdcardOutlined,
  HistoryOutlined,
  BellOutlined,
  SoundOutlined,
  BankOutlined,
  CloudDownloadOutlined,
  FundProjectionScreenOutlined,
  GlobalOutlined,
  FontColorsOutlined
} from '@ant-design/icons-vue'

const route = useRoute()
const router = useRouter()
useUserStore() // 仅用于确保 store 初始化（权限管理器依赖），菜单本身基于 permissionManager
const appStore = useAppStore()
const { t, locale } = useI18n()

type NavMenuItem = {
  key: string
  title: string
  titleKey?: string
  path: string
  icon: string
  children?: NavMenuItem[]
}

/** 菜单标题：优先 meta.titleKey 以支持 i18n */
function resolveMenuTitle(item: Pick<NavMenuItem, 'title' | 'titleKey'>) {
  return item.titleKey ? t(item.titleKey) : item.title
}

// 响应式数据
const selectedKeys = ref<string[]>([])
const openKeys = ref<string[]>([])

// 图标组件映射
const iconComponents = {
  DashboardOutlined,
  ProjectOutlined,
  VideoCameraOutlined,
  FileTextOutlined,
  CheckSquareOutlined,
  SettingOutlined,
  UserOutlined,
  FolderOutlined,
  FileAddOutlined,
  UnorderedListOutlined,
  SendOutlined,
  InboxOutlined,
  TeamOutlined,
  ApartmentOutlined,
  SafetyOutlined,
  DatabaseOutlined,
  ApiOutlined,
  BookOutlined,
  ControlOutlined,
  IdcardOutlined,
  HistoryOutlined,
  BellOutlined,
  SoundOutlined,
  BankOutlined,
  CloudDownloadOutlined,
  FundProjectionScreenOutlined,
  GlobalOutlined,
  FontColorsOutlined
}

// 计算属性
const collapsed = computed(() => appStore.collapsed)

// 从路由配置构建菜单树：仅依赖 meta.title / meta.icon / meta.permissions
function buildMenusFromRoutes(routes: RouteRecordRaw[], parentPath = ''): NavMenuItem[] {
  const result: NavMenuItem[] = []

  routes.forEach((route) => {
    // 隐藏或无标题的路由不参与侧边菜单
    if (route.meta?.hidden) return
    if (!route.meta?.title) return

    // 使用 permissionManager 检查当前用户是否有权访问该路由
    if (!permissionManager.checkRoutePermission(route)) return

    // 计算完整路径：父路径 + 当前路径
    const rawPath = route.path || ''
    const fullPath = rawPath.startsWith('/')
      ? rawPath
      : `${parentPath}/${rawPath}`.replace(/\/+/g, '/')

    // 若既没有组件、也没有子路由（例如测试占位路由），则不作为菜单项
    const hasChildren = Array.isArray(route.children) && route.children.length > 0
    if (!route.component && !hasChildren) {
      return
    }

    const item: NavMenuItem = {
      key: (route.name as string) || route.path,
      title: route.meta.title as string,
      titleKey: route.meta.titleKey as string | undefined,
      path: fullPath,
      icon: (route.meta.icon as string) || ''
    }

    if (hasChildren) {
      const children = buildMenusFromRoutes(route.children as RouteRecordRaw[], fullPath)
      if (children.length > 0) {
        item.children = children
      } else if (!route.component) {
        return
      }
    }

    result.push(item)
  })

  return result
}

// 根据路由树和权限生成最终菜单（locale 参与依赖，切换语言时重建）
const filteredMenus = computed<NavMenuItem[]>(() => {
  void locale.value
  // 找到主布局下的 children 作为菜单根
  const mainLayoutRoute = appRouter.options.routes.find(
    (r) => r.path === '/' && Array.isArray(r.children) && r.children.length > 0
  )
  const childRoutes = (mainLayoutRoute?.children as RouteRecordRaw[]) || []
  return buildMenusFromRoutes(childRoutes)
})

// 获取图标组件
const getIconComponent = (iconName?: string) => {
  if (!iconName) return null
  return iconComponents[iconName as keyof typeof iconComponents] || null
}

// 更新菜单状态
const updateMenuState = (path: string) => {
  selectedKeys.value = [path]
  
  // 自动展开父级菜单（当前路径匹配该菜单或其子项时展开）
  if (!collapsed.value) {
    const parentMenu = filteredMenus.value.find(menu => {
      if (!menu.children?.length) return false
      const pathMatches = menu.path === path ||
        menu.children.some((c: { path?: string }) => c.path === path)
      return pathMatches
    })
    if (parentMenu) {
      openKeys.value = [parentMenu.key]
    }
  }
}

// 监听路由变化更新菜单选中状态
watch(() => route.path, (newPath) => {
  updateMenuState(newPath)
}, { immediate: true })

// 监听折叠状态变化
watch(collapsed, (newCollapsed) => {
  if (newCollapsed) {
    openKeys.value = []
  } else {
    // 展开时恢复当前路由对应的父菜单
    updateMenuState(route.path)
  }
})

// 菜单点击处理
const handleMenuClick = ({ key }: { key: string }) => {
  if (key !== route.path) {
    router.push(key)
  }
}

// 初始化
onMounted(() => {
  updateMenuState(route.path)
})
</script>

<style lang="less" scoped>
.side-navigation {
  height: 100%;
  
  .navigation-menu {
    border-right: none;
    background: transparent;
    
    :deep(.ant-menu-item) {
      margin: 4px 8px;
      border-radius: 8px;
      height: 40px;
      line-height: 40px;
      font-size: 14px;
      transition: all 0.3s;
      
      &.ant-menu-item-selected {
        background: linear-gradient(135deg, #1890ff, #36cfc9);
        color: #fff;
        font-weight: 500;
        
        &::after {
          display: none;
        }
        
        .anticon {
          color: #fff;
        }
      }
      
      &:hover:not(.ant-menu-item-selected) {
        background-color: #f0f9ff;
        color: #1890ff;
        
        .anticon {
          color: #1890ff;
        }
      }
      
      .anticon {
        font-size: 16px;
        margin-right: 8px;
        transition: color 0.3s;
      }
      
      // 折叠状态下的样式
      &.ant-menu-item-only-child {
        text-align: center;
        
        .anticon {
          margin-right: 0;
        }
      }
    }
    
    :deep(.ant-menu-submenu) {
      margin: 4px 8px;
      
      .ant-menu-submenu-title {
        border-radius: 8px;
        height: 40px;
        line-height: 40px;
        font-size: 14px;
        transition: all 0.3s;
        
        &:hover {
          background-color: #f0f9ff;
          color: #1890ff;
          
          .anticon {
            color: #1890ff;
          }
        }
        
        .anticon {
          font-size: 16px;
          margin-right: 8px;
          transition: color 0.3s;
        }
      }
      
      &.ant-menu-submenu-selected {
        .ant-menu-submenu-title {
          color: #1890ff;
          
          .anticon {
            color: #1890ff;
          }
        }
      }
      
      .ant-menu-submenu-arrow {
        font-size: 12px;
        transition: transform 0.3s;
      }
      
      &.ant-menu-submenu-open {
        .ant-menu-submenu-arrow {
          transform: rotate(180deg);
        }
      }
    }
    
    :deep(.ant-menu-sub) {
      background: transparent;
      
      .ant-menu-item {
        height: 36px;
        line-height: 36px;
        font-size: 13px;
        padding-left: 40px !important;
        margin: 2px 8px;
        
        .anticon {
          font-size: 14px;
          margin-right: 6px;
        }
        
        /* 子菜单不设选中底色，与「翻译历史」等项样式一致 */
        &.ant-menu-item-selected {
          background: transparent;
          color: inherit;
          
          .anticon {
            color: inherit;
          }
        }
        
        /* 子项悬停效果统一：选中与未选中一致 */
        &:hover {
          background-color: #f0f9ff;
          color: #1890ff;
          
          .anticon {
            color: #1890ff;
          }
        }
      }
    }
    
    // 折叠状态下的样式调整
    &.ant-menu-inline-collapsed {
      :deep(.ant-menu-item) {
        padding: 0 calc(50% - 8px);
        text-align: center;
        
        .anticon {
          margin: 0;
          font-size: 18px;
        }
      }
      
      :deep(.ant-menu-submenu) {
        .ant-menu-submenu-title {
          padding: 0 calc(50% - 8px);
          text-align: center;
          
          .anticon {
            margin: 0;
            font-size: 18px;
          }
        }
      }
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .side-navigation {
    .navigation-menu {
      :deep(.ant-menu-item) {
        height: 44px;
        line-height: 44px;
        font-size: 15px;
        margin: 4px 12px;
        
        .anticon {
          font-size: 18px;
          margin-right: 10px;
        }
      }
      
      :deep(.ant-menu-submenu) {
        .ant-menu-submenu-title {
          height: 44px;
          line-height: 44px;
          font-size: 15px;
          
          .anticon {
            font-size: 18px;
            margin-right: 10px;
          }
        }
      }
      
      :deep(.ant-menu-sub .ant-menu-item) {
        height: 40px;
        line-height: 40px;
        font-size: 14px;
        padding-left: 48px !important;
        
        .anticon {
          font-size: 16px;
          margin-right: 8px;
        }
      }
    }
  }
}

// 暗色主题支持
@media (prefers-color-scheme: dark) {
  .side-navigation {
    .navigation-menu {
      :deep(.ant-menu-item:hover:not(.ant-menu-item-selected)) {
        background-color: rgba(24, 144, 255, 0.1);
      }
      
      :deep(.ant-menu-submenu-title:hover) {
        background-color: rgba(24, 144, 255, 0.1);
      }
    }
  }
}

// 动画效果
.navigation-menu {
  :deep(.ant-menu-item),
  :deep(.ant-menu-submenu-title) {
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: left 0.5s;
    }
    
    &:hover::before {
      left: 100%;
    }
  }
}
</style>